/**
 * One presentation model for a pull request, shared by every CHEW surface.
 *
 * Built around absence: a row says nothing about who holds a review, how old it
 * is, or what is wrong with it unless one of those is true. A quiet row is a
 * review nobody has touched and nothing is wrong with — the common case, and
 * the one a reviewer should be able to pick out without reading.
 */
import type { QueueEntry } from "../../shared/global-queue";
import { liveReviewPath } from "../../shared/pull-routes";
import type { PullBadgeFacts } from "../components/PullPresentation";
import type { OutstandingPull } from "./hosted";

/** Urgency, not category. Only ever applied to something that departs from normal. */
export type PullTone = "critical" | "attention" | "positive" | "quiet";

/**
 * The single status channel the strip carries. This is the only place colour
 * is allowed to originate in a queue row; everything else is ink and mono.
 */
export type PullChannel = "free" | "owed" | "mine" | "held" | "blocked" | "authored" | "settled";

export type PullGlyph = "fail" | "running" | "comment" | "pen" | "clock";

export interface PullException {
  key: string;
  glyph: PullGlyph;
  /** Full sentence; shown as a tooltip and to screen readers, not inline. */
  label: string;
  count: number | null;
  tone: PullTone;
}

export interface PullIdentity {
  repository: string;
  pullNumber: number;
  title: string;
  url: string;
  reviewPath: string;
}

export interface PullView {
  key: string;
  identity: PullIdentity;
  /** Repository, number, author and size — the identity every surface renders. */
  badge: PullBadgeFacts;
  channel: PullChannel;
  /** Only ever set when a person actually holds this review; absence means free. */
  holder: { login: string; label: string; detail: string; isViewer: boolean } | null;
  exceptions: PullException[];
  estimate: { label: string; title: string } | null;
  age: { label: string; title: string; tone: PullTone } | null;
  /** Long form, for detail panes and screen readers. */
  state: { label: string; detail: string };
}

export function repositoryShortName(repository: string): string {
  return repository.split("/").filter(Boolean).at(-1) ?? repository;
}

/** Compact elapsed time: `4m`, `6h`, `3d`. Null when the timestamp says nothing. */
export function sinceLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const elapsed = Date.now() - Date.parse(value);
  if (!Number.isFinite(elapsed) || elapsed < 0) return null;
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function absoluteLabel(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toLocaleString() : undefined;
}

export function compactCount(value: number): string {
  return Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

export function pullKey(repository: string, pullNumber: number): string {
  return `${repository}#${pullNumber}`;
}

function identityFor(entry: QueueEntry): PullIdentity {
  return {
    repository: entry.repository,
    pullNumber: entry.pullNumber,
    title: entry.title,
    url: entry.url ?? `https://github.com/${entry.repository}/pull/${entry.pullNumber}`,
    reviewPath: entry.reviewPath ?? liveReviewPath(entry.repository, entry.pullNumber),
  };
}

function stateFor(entry: QueueEntry): { label: string; detail: string } {
  const detail = entry.reasonDetail;
  if (entry.reasonCode === "source-refreshing") return { label: "Syncing", detail };
  if (entry.status === "error") return { label: "Sync failed", detail };
  if (entry.lifecycle === "draft") return { label: "Draft", detail };
  if (entry.reasonCode === "changes-requested") return { label: "Changes requested", detail };
  if (entry.status === "satisfied") return { label: "Approved", detail };
  if (entry.status === "blocked") return { label: "Blocked", detail };
  if (entry.status === "in-review") return { label: "In review", detail };
  if (entry.reasonCode === "claimed") return { label: "Claimed", detail };
  if (entry.status === "assigned") return { label: "Assigned", detail };
  return { label: "Ready", detail };
}

function sameLogin(left: string | null | undefined, right: string | undefined): boolean {
  return Boolean(left && right && left.toLocaleLowerCase() === right.toLocaleLowerCase());
}

function holderFor(entry: QueueEntry, viewerLogin: string | undefined): PullView["holder"] {
  const login = entry.claim?.reviewerLogin ?? entry.activeReviewer;
  if (!login) return null;
  const isViewer = sameLogin(login, viewerLogin);
  const verb = entry.claim ? "claimed" : "is reviewing";
  return {
    login,
    label: isViewer ? "you" : login,
    detail: isViewer ? `You ${entry.claim ? "hold the claim on" : "have the active review on"} this.` : `@${login} ${verb} this.`,
    isViewer,
  };
}

function channelFor(entry: QueueEntry, holder: PullView["holder"]): PullChannel {
  if (entry.forMe.reasonCode === "author") return "authored";
  if (entry.status === "satisfied" || entry.lifecycle === "draft") return "settled";
  if (entry.status === "blocked" || entry.status === "error") return "blocked";
  if (holder?.isViewer || ["claimed-by-self", "in-review-by-self"].includes(entry.forMe.outcome)) return "mine";
  if (entry.forMe.outcome === "assigned") return "owed";
  if (holder) return "held";
  return "free";
}

function exceptionsFor(entry: QueueEntry): PullException[] {
  const found: PullException[] = [];
  const { ci, conversation } = entry.attention;
  if (entry.reasonCode === "changes-requested") {
    found.push({ key: "changes", glyph: "pen", label: "Changes requested", count: null, tone: "attention" });
  }
  if (ci?.state === "failure") {
    found.push({ key: "ci", glyph: "fail", label: `${ci.failed} of ${ci.total} checks failing`, count: ci.failed, tone: "critical" });
  } else if (ci?.state === "pending") {
    found.push({ key: "ci", glyph: "running", label: `${ci.pending} checks still running`, count: ci.pending, tone: "quiet" });
  }
  if (conversation && conversation.unresolvedThreads > 0) {
    found.push({
      key: "threads",
      glyph: "comment",
      label: `${conversation.unresolvedThreads} unresolved ${conversation.unresolvedThreads === 1 ? "conversation" : "conversations"}`,
      count: conversation.unresolvedThreads,
      tone: "attention",
    });
  }
  if (entry.chewGate === "chewfile-stale") {
    found.push({ key: "guide", glyph: "clock", label: "Review guide is stale", count: null, tone: "quiet" });
  }
  return found;
}

/** Waiting stops being neutral at two days and turns urgent at a week. */
function ageFor(entry: QueueEntry): PullView["age"] {
  const at = entry.waitSince ?? entry.githubUpdatedAt;
  const label = sinceLabel(at);
  if (!label || !at) return null;
  const days = (Date.now() - Date.parse(at)) / 86_400_000;
  return {
    label,
    title: `Waiting since ${absoluteLabel(at)}`,
    tone: days >= 7 ? "critical" : days >= 2 ? "attention" : "quiet",
  };
}

export function pullView(entry: QueueEntry, viewerLogin?: string): PullView {
  const holder = holderFor(entry, viewerLogin);
  return {
    key: pullKey(entry.repository, entry.pullNumber),
    identity: identityFor(entry),
    badge: {
      repository: entry.repository,
      pullNumber: entry.pullNumber,
      author: entry.authorLogin,
      additions: entry.stats.additions,
      deletions: entry.stats.deletions,
      changedFiles: entry.stats.changedFiles,
    },
    channel: channelFor(entry, holder),
    holder,
    exceptions: exceptionsFor(entry),
    estimate: entry.estimatedMinutes
      ? { label: `~${entry.estimatedMinutes}m`, title: `About ${entry.estimatedMinutes} minutes to review` }
      : null,
    age: ageFor(entry),
    state: stateFor(entry),
  };
}

export type QueueBandKey = "blocked" | "owed" | "mine" | "free" | "held" | "author" | "settled";

/** Reading order is the product opinion: what is asked of you, then what you can take. */
export const queueBands: Array<{ key: QueueBandKey; title: string; blurb: string }> = [
  { key: "blocked", title: "Stuck", blurb: "Blocked, or CHEW could not sync them" },
  { key: "owed", title: "Asked of you", blurb: "Someone named you as the reviewer" },
  { key: "mine", title: "You have these", blurb: "Claimed, or already under your review" },
  { key: "free", title: "Free to take", blurb: "Nobody has picked these up" },
  { key: "held", title: "Being reviewed", blurb: "Someone else is on it" },
  { key: "author", title: "Back with the author", blurb: "Waiting on a push or a re-request" },
  { key: "settled", title: "Settled", blurb: "Approved, draft, or nothing left to do" },
];

export function bandFor(view: PullView, entry: QueueEntry): QueueBandKey {
  if (view.channel === "blocked") return "blocked";
  if (entry.reasonCode === "changes-requested") return "author";
  if (view.channel === "authored" || view.channel === "settled") return "settled";
  if (view.channel === "mine") return "mine";
  if (view.channel === "owed") return "owed";
  if (view.channel === "held") return "held";
  return "free";
}

export const outcomePriority: Record<QueueEntry["forMe"]["outcome"], number> = {
  "in-review-by-self": 0,
  "claimed-by-self": 1,
  assigned: 2,
  available: 3,
  ineligible: 4,
};

/** Everything actionable by the viewer, best first. One ranking, every surface. */
export function rankedForViewer(entries: QueueEntry[] | undefined): QueueEntry[] {
  return (entries ?? [])
    .filter((entry) => entry.forMe.outcome !== "ineligible" && !entry.forMe.skipped)
    .sort((left, right) => outcomePriority[left.forMe.outcome] - outcomePriority[right.forMe.outcome]
      || (left.forMe.eligibleRank ?? Number.MAX_SAFE_INTEGER) - (right.forMe.eligibleRank ?? Number.MAX_SAFE_INTEGER)
      || left.position - right.position);
}

const authoredStates: Record<OutstandingPull["status"], { label: string; detail: string }> = {
  "changes-requested": { label: "Changes requested", detail: "A reviewer is waiting on you." },
  ready: { label: "Awaiting review", detail: "Sent for review." },
  approved: { label: "Approved", detail: "Ready to land." },
  draft: { label: "Draft", detail: "Not sent for review." },
};

/** The same model for a PR the viewer authored, where the holder is the reviewer side. */
export function authoredPullView(pull: OutstandingPull): PullView {
  const awaiting = pull.reReviewNeeded?.length ? pull.reReviewNeeded : pull.requestedReviewers;
  const exceptions: PullException[] = [];
  if (pull.attention.ci?.state === "pending" && pull.attention.ci.pending > 0) {
    exceptions.push({ key: "ci", glyph: "running", label: `${pull.attention.ci.pending} checks still running`, count: pull.attention.ci.pending, tone: "quiet" });
  }
  if (pull.attention.ci?.state === "failure") {
    exceptions.push({ key: "ci", glyph: "fail", label: `${pull.attention.ci.failed} of ${pull.attention.ci.total} checks failing`, count: pull.attention.ci.failed, tone: "critical" });
  }
  if (pull.attention.conversation && pull.attention.conversation.unresolvedThreads > 0) {
    exceptions.push({ key: "threads", glyph: "comment", label: `${pull.attention.conversation.unresolvedThreads} unresolved conversations`, count: pull.attention.conversation.unresolvedThreads, tone: "attention" });
  }
  const loc = pull.loc?.changedLoc ?? null;
  return {
    key: pullKey(pull.repository, pull.pullNumber),
    identity: {
      repository: pull.repository,
      pullNumber: pull.pullNumber,
      title: pull.title,
      url: pull.url,
      reviewPath: pull.authoring.url,
    },
    badge: {
      repository: pull.repository,
      pullNumber: pull.pullNumber,
      changedLoc: loc ?? null,
      changedFiles: null,
    },
    channel: "authored",
    holder: awaiting?.length
      ? { login: awaiting[0]!, label: awaiting.join(", "), detail: `Waiting on ${awaiting.map((login) => `@${login}`).join(", ")}.`, isViewer: false }
      : null,
    exceptions,
    estimate: null,
    age: null,
    state: authoredStates[pull.status],
  };
}
